import { useAccountProfileMutation } from '@mote/api'
import { Avatar, Block, BlockGroup, Button, Spinner, Text } from '@mote/ui'
import React from 'react'
import styled from 'styled-components'
import { getMoteLogin } from '../../../shared/Auth'
import { AccountProfileDeleteAvatarOverlay } from './AccountProfileDeleteAvatarOverlay'

const maxFileSize = 2 * 1024 * 1024

export const AccountProfileAvatar = ({ avatarUrl, name, updateUser }) => {
  const inputFileRef = React.useRef(null)
  const [fileSizeError, setFileSizeError] = React.useState(null)

  const {
    mutate,
    loading,
    error: uploadingAvatarError
  } = useAccountProfileMutation()

  function handleFileInputClick() {
    inputFileRef.current?.click()
  }

  function handleDeleteAvatar() {
    mutate({
      variables: {
        avatar: {
          remove: true
        }
      }
    }).then(() => updateUser(getMoteLogin()))
  }

  async function handleFileChangeCapture(e) {
    if (fileSizeError) {
      setFileSizeError(null)
    }
    if (!e.target.files.length) return
    const file = e.target.files[0]
    if (file.size > maxFileSize) {
      setFileSizeError(`File size must be less than 2MB`)
      return
    }
    const b64Image = await getBase64(file)

    await mutate({
      variables: {
        avatar: {
          contentType: file.type,
          base64content: b64Image
        }
      }
    })

    inputFileRef.current.value = ''

    updateUser(getMoteLogin())
  }

  function getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result.split('base64,')[1])
      reader.onerror = error =>
        reject(error instanceof Error ? error : new Error(error))
    })
  }

  return (
    <Root gap={16}>
      <Block layout='horizontal' gap={16} align='center'>
        {!loading && (
          <AvatarContainer>
            <Avatar text={name} url={avatarUrl} />

            <AccountProfileDeleteAvatarOverlay
              url={avatarUrl}
              onDeleteAvatar={handleDeleteAvatar}
            />
          </AvatarContainer>
        )}
        {loading && (
          <LoadingIndicator>
            <Spinner radius={24} color='#25252D' />
          </LoadingIndicator>
        )}
        <Button
          text='Upload'
          onClick={handleFileInputClick}
          variant='soft'
          disabled={loading}
        />
        <input
          style={{ display: 'none' }}
          type='file'
          ref={inputFileRef}
          onChange={handleFileChangeCapture}
          multiple={false}
          accept={['image/png', 'image/jpeg', 'image/jpg'].join(',')}
        />
      </Block>
      <BlockGroup>
        {(fileSizeError || uploadingAvatarError) && (
          <Block margin='0 0 10px 0' maxWidth={300}>
            <Text
              fontSize={13}
              color='#F44336'
              fontWeight={400}
              lineHeight='140%'
            >
              {uploadingAvatarError
                ? 'An unknown error occurred. If the problem persists kindly contact support'
                : fileSizeError}
            </Text>
          </Block>
        )}
        <Text fontSize={13} color='#6C6C84' fontWeight={400} lineHeight='140%'>
          Image must be square and at least 140x140px.{' '}
        </Text>
        <Text fontSize={13} color='#6C6C84' fontWeight={400} lineHeight='140%'>
          Max file size: 2MB. File types accepted: JPG, PNG.
        </Text>
      </BlockGroup>
    </Root>
  )
}

const Root = styled(Block)``
const LoadingIndicator = styled.div`
  width: 80px;
  height: 80px;
  background: #ececf3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`
const AvatarContainer = styled.div`
  position: relative;
`
